"use client";

/**
 * WatchlistPanel — RightRail "관심 종목" 탭 본문 (사용자 결정 2026-05-21).
 *
 * 데이터: localStorage 'dashboard:watchlist' (hero-search 와 공유)
 *   - 추가: 입력 + Enter → ticker.toUpperCase() (영문) 또는 6자리 숫자
 *   - 제거: X 버튼
 *   - 클릭: /analyze/[ticker]
 *   - 가격: /api/ticker/prices 배치 fetch
 *
 * 다른 탭으로 이동했다가 돌아와도 state 유지 (localStorage 영속).
 */

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { Star, Plus, X } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "dashboard:watchlist";
const PRICES_CACHE_KEY = "dashboard:watchlist:prices";
const PRICES_CACHE_TTL_MS = 2 * 60 * 1000; // 2분
const MAX_ITEMS = 30;

interface PriceInfo {
  price: number | null;
  changePercent: number | null;
}

// 사용자 보고 2026-05-23: backend /prices Yahoo rate-limit 시 stooq fallback 으로 수십초.
// sessionStorage 에 prices cache (2분 TTL) — 즉시 표시 + background refresh.
function readPricesCache(): { ts: number; data: Record<string, PriceInfo> } | null {
  try {
    const raw = window.sessionStorage.getItem(PRICES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.ts === "number" && parsed.data) return parsed;
  } catch {}
  return null;
}
function writePricesCache(data: Record<string, PriceInfo>) {
  try {
    window.sessionStorage.setItem(PRICES_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

function normalize(raw: string): string {
  const cleaned = raw.trim().replace(/\s+/g, "").replace(/^\$/, "");
  if (/^\d{6}$/.test(cleaned)) return cleaned;
  const us = cleaned.match(/^([A-Za-z]{1,5})(?:\.[A-Za-z]+)?$/);
  if (us) return us[1].toUpperCase();
  return cleaned.toUpperCase();
}

export function WatchlistPanel() {
  const [items, setItems] = useState<string[]>([]);
  const [prices, setPrices] = useState<Record<string, PriceInfo>>({});
  const [input, setInput] = useState("");

  // localStorage 복원
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setItems(arr.map(String).slice(0, MAX_ITEMS));
      }
    } catch { /* ignore */ }
  }, []);

  // items 변경 시 localStorage 저장 + 가격 batch fetch
  const persist = useCallback((next: string[]) => {
    setItems(next);
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }, []);

  useEffect(() => {
    if (items.length === 0) { setPrices({}); return; }
    let cancelled = false;

    // 1) sessionStorage cache 즉시 표시 (TTL 안일 때만)
    const cached = readPricesCache();
    if (cached && Date.now() - cached.ts < PRICES_CACHE_TTL_MS) {
      // items 와 cached keys 가 모두 매칭 시 즉시 표시
      const allHit = items.every((t) => cached.data[t]);
      if (allHit) setPrices(cached.data);
    }

    // 2) Background fetch — cache 가 stale 이면 갱신
    (async () => {
      try {
        const data = await api<{ results?: Record<string, PriceInfo> }>(
          "/api/ticker/prices",
          { method: "POST", body: { tickers: items } }
        );
        if (cancelled) return;
        const fresh = data.results || {};
        setPrices(fresh);
        writePricesCache(fresh);
      } catch { /* ignore — cache 표시 유지 */ }
    })();
    return () => { cancelled = true; };
  }, [items]);

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const t = normalize(input);
    if (!t || items.includes(t) || items.length >= MAX_ITEMS) {
      setInput("");
      return;
    }
    persist([...items, t]);
    setInput("");
  };

  const onRemove = (t: string) => {
    persist(items.filter((x) => x !== t));
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* 추가 input */}
      <form onSubmit={onAdd} className="flex items-stretch gap-[var(--bs-space-2)] p-[var(--bs-space-3)] border-b border-[var(--bs-gray-200)]">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="티커 추가 (예: AAPL, 005930)"
          maxLength={16}
          className="flex-1 h-9 px-3 rounded-[var(--bs-radius-md)] border border-[var(--bs-gray-200)] bg-[var(--bs-paper)] text-[length:var(--bs-fs-sm)] font-semibold text-[var(--bs-ink)] outline-none focus:border-[var(--bs-red-500)]"
        />
        <button
          type="submit"
          disabled={!input.trim() || items.length >= MAX_ITEMS}
          className="h-9 px-3 rounded-[var(--bs-radius-md)] bg-[var(--bs-ink)] text-white text-[length:var(--bs-fs-sm)] font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1"
          aria-label="추가"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </form>

      {/* 목록 */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-[var(--bs-space-8)] text-center px-[var(--bs-space-4)]">
            <Star className="w-5 h-5 text-[color:var(--bs-gray-400)]" />
            <p className="m-0 text-[length:var(--bs-fs-sm)] text-[color:var(--bs-gray-500)]">
              관심 종목을 추가하면 여기에 표시됩니다
            </p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {items.map((t, i) => {
              const p = prices[t];
              const changePct = p?.changePercent;
              const isKr = /^\d{6}$/.test(t);
              const priceStr = p?.price != null
                ? (isKr ? `₩${Math.round(p.price).toLocaleString("ko-KR")}` : `$${p.price.toFixed(2)}`)
                : "";
              const pctColor = changePct == null
                ? "text-[color:var(--bs-gray-500)]"
                : changePct >= 0 ? "text-[color:var(--bs-up)]" : "text-[color:var(--bs-down)]";
              return (
                <li key={t} className={`group flex items-center gap-2 px-[var(--bs-space-3)] py-[var(--bs-space-2)] hover:bg-[var(--bs-red-50)] ${i > 0 ? "border-t border-[var(--bs-gray-100)]" : ""}`}>
                  <Link href={`/analyze/${t.toLowerCase()}`} className="flex-1 min-w-0 flex items-baseline gap-2 no-underline">
                    <span className="text-[length:var(--bs-fs-sm)] font-bold text-[color:var(--bs-ink)] tracking-tight">{t}</span>
                    {priceStr && (
                      <span className="text-[length:var(--bs-fs-xs)] text-[color:var(--bs-gray-700)] truncate" style={{ fontVariantNumeric: "tabular-nums" }}>
                        {priceStr}
                      </span>
                    )}
                    {changePct != null && (
                      <span className={`text-[length:var(--bs-fs-xs)] font-bold ${pctColor} ml-auto`} style={{ fontVariantNumeric: "tabular-nums" }}>
                        {changePct >= 0 ? "+" : ""}{(changePct * 100).toFixed(2)}%
                      </span>
                    )}
                  </Link>
                  <button
                    type="button"
                    onClick={() => onRemove(t)}
                    aria-label={`${t} 제거`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-[var(--bs-gray-200)] text-[color:var(--bs-gray-500)] hover:text-[color:var(--bs-ink)]"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
